import * as React from 'react';
import { Redirect, Route, Switch, useLocation, useParams, useRouteMatch } from 'react-router-dom';

import { VIEW } from '../services/shared/common';
import { useWorldRoutes } from './parts/allWorldRoutes';

// The standalone /battery pages are gone - BatteryCentricContainer/BatteryDevicesContainer/
// BatteryOverviewContainer now live inside the Core Batteries hub (see
// containers/core/batteriesHub). These routes exist purely so old bookmarks, alert-email links
// and legacy :view-param URLs still work, by redirecting into the hub and translating :view
// into the ?status= query the hub's containers already understand, while preserving id/ts and
// any other existing query params.
const batteryRouteMapping: Record<string, VIEW> = {
  [VIEW.all]: VIEW.all,
  undefined: VIEW.all,
  "unknown": VIEW.all,
  [VIEW.red]: VIEW.red,
  "replace": VIEW.red,
  "problem": VIEW.red,
  [VIEW.orange]: VIEW.orange,
  "warning": VIEW.orange,
  [VIEW.green]: VIEW.green,
  "good": VIEW.green
};

const deviceRouteMapping: Record<string, VIEW> = {
  [VIEW.all]: VIEW.all,
  undefined: VIEW.all,
  "unknown": VIEW.all,
  [VIEW.red]: VIEW.red,
  "problem": VIEW.red,
  [VIEW.yellow]: VIEW.yellow,
  "warning": VIEW.yellow,
  [VIEW.green]: VIEW.green,
  "good": VIEW.green
};

export const BatteryRoutes = () => {
  const basePath = useRouteMatch().path;
  const routes = useWorldRoutes();

  return (
    <Switch>
      <Route exact path={`${basePath}/overview`}>
        <ViewRedirect target={routes.batteryEssentials.overview} />
      </Route>
      <Route exact path={[`${basePath}/devices/:view`, `${basePath}/devices`]}>
        <ViewRedirect target={routes.batteryEssentials.devices} mapping={deviceRouteMapping} />
      </Route>
      <Route exact path={[`${basePath}/:view`, basePath]}>
        <ViewRedirect target={routes.batteryEssentials.battery} mapping={batteryRouteMapping} orangeAsYellow />
      </Route>
    </Switch>
  );
};

interface ViewRedirectProps {
  target: string,
  mapping?: Record<string, VIEW>,
  orangeAsYellow?: boolean
}

const ViewRedirect = (props: ViewRedirectProps) => {
  const { target, mapping, orangeAsYellow } = props;
  const { view } = useParams<{ view: string }>();
  const { search } = useLocation();

  const newParams = new URLSearchParams(search);
  if (mapping && view) {
    const normalizedView = mapping[view] || mapping[VIEW.all];
    if (normalizedView !== VIEW.all) {
      const statusValue = orangeAsYellow && normalizedView === VIEW.orange ? VIEW.yellow : normalizedView;
      newParams.set('status', statusValue);
    }
  }

  const queryString = newParams.toString();
  return <Redirect to={queryString ? `${target}?${queryString}` : target} />;
};
